import { ExternalLink } from "lucide-react";

/* ── Spotify icon (simplified) ── */
function SpotifyIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381C8.64 5.801 15.6 6.06 20.04 8.82c.54.3.72 1.02.42 1.56-.3.42-1.02.6-1.56.3h.18z" />
    </svg>
  );
}

/* ── Apple Podcasts icon (simplified) ── */
function ApplePodcastsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 3.6c4.636 0 8.4 3.764 8.4 8.4 0 2.809-1.38 5.295-3.498 6.822l-.372-1.152c1.74-1.284 2.87-3.348 2.87-5.67 0-3.866-3.134-7-7-7s-7 3.134-7 7c0 2.322 1.13 4.386 2.87 5.67l-.372 1.152C5.78 17.295 4.4 14.809 4.4 12c0-4.636 3.764-8.4 8.4-8.4zm0 3.6a4.8 4.8 0 0 0-3.24 8.34l-.72 2.16A7.19 7.19 0 0 1 4.8 12 7.2 7.2 0 0 1 12 4.8a7.2 7.2 0 0 1 7.2 7.2 7.19 7.19 0 0 1-3.24 5.1l-.72-2.16A4.8 4.8 0 0 0 12 7.2zm0 3.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8zm-1.08 5.94c-.18.84-.36 1.8-.3 2.52.06.84.36 1.2.96 1.38.36.12.6.12.96.12s.6 0 .96-.12c.6-.18.9-.54.96-1.38.06-.72-.12-1.68-.3-2.52l-.48-1.8a2.4 2.4 0 0 1-2.88 0l-.48 1.8z" />
    </svg>
  );
}

interface PlatformBadgeProps {
  href: string;
  platform: "spotify" | "apple";
}

export function PlatformBadge({ href, platform }: PlatformBadgeProps) {
  if (platform === "spotify") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="comic-play-btn relative overflow-hidden inline-flex items-center gap-2 px-4 py-2 border-[3px] font-mono font-bold text-xs
          bg-[hsl(141,73%,90%)] text-[hsl(141,69%,20%)] border-[hsl(141,69%,30%)] rounded-md
          shadow-[4px_4px_0px_0px_hsl(var(--foreground))] hover:shadow-[2px_2px_0px_0px_hsl(var(--foreground))]
          hover:translate-x-[2px] hover:translate-y-[2px]
          transition-all duration-150 transform hover:rotate-[-0.5deg] hover:scale-[1.03] active:scale-95"
      >
        <SpotifyIcon className="w-5 h-5 text-[hsl(141,73%,42%)] relative z-10" />
        <span className="relative z-10">Listen on Spotify</span>
        <ExternalLink className="w-3 h-3 opacity-50 relative z-10" />
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="comic-play-btn relative overflow-hidden inline-flex items-center gap-2 px-4 py-2 border-[3px] font-mono font-bold text-xs
        bg-[hsl(280,60%,92%)] text-[hsl(280,60%,25%)] border-[hsl(280,50%,40%)] rounded-md
        shadow-[4px_4px_0px_0px_hsl(var(--foreground))] hover:shadow-[2px_2px_0px_0px_hsl(var(--foreground))]
        hover:translate-x-[2px] hover:translate-y-[2px]
        transition-all duration-150 transform hover:rotate-[-0.5deg] hover:scale-[1.03] active:scale-95"
    >
      <ApplePodcastsIcon className="w-5 h-5 text-[hsl(280,80%,50%)] relative z-10" />
      <span className="relative z-10">Listen on Apple Podcasts</span>
      <ExternalLink className="w-3 h-3 opacity-50 relative z-10" />
    </a>
  );
}

export function PlatformBadges({ spotifyUrl, appleUrl }: { spotifyUrl?: string | null; appleUrl?: string | null }) {
  if (!spotifyUrl && !appleUrl) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {spotifyUrl && <PlatformBadge href={spotifyUrl} platform="spotify" />}
      {appleUrl && <PlatformBadge href={appleUrl} platform="apple" />}
    </div>
  );
}
